import { IEFinderObject, IEpisodeDraft } from '@model';
import { normalizeImageForUpload } from '@utils/imageUpload';
import { toQueryString } from '@utils/misc';

import { api, defaultTransformResponse } from '.';
import type {
  LiveMediaMode,
  MediaLiveKind,
} from '@base/lib/live-media/domain';
import { LIVE_MEDIA_PIPELINE_VERSION } from '@base/lib/live-media/domain';

export type LiveKind = 'chat' | MediaLiveKind;
export type { LiveMediaMode } from '@base/lib/live-media/domain';
export type LiveStatus = 'preparing' | 'live' | 'ended';
export type LiveEndReason =
  | ''
  | 'author'
  | 'heartbeat_timeout'
  | 'restriction'
  | 'preparation_timeout'
  | 'media_disconnected'
  | 'media_provider_failure';

export interface LiveUser {
  id: number;
  nickname: string | null;
  picture?: IEFinderObject<'image'> | null;
}

export interface LiveSession {
  id: number;
  author: LiveUser | null;
  title: string;
  kind: LiveKind;
  mediaMode: LiveMediaMode;
  status: LiveStatus;
  startedAt: string;
  endedAt: string | null;
  endReason: LiveEndReason;
  latestSequence: number;
  heartbeatIntervalSeconds: number;
  viewerCount: number;
}

export type CurrentLiveSession = Omit<LiveSession, 'status'> & {
  status: Extract<LiveStatus, 'preparing' | 'live'>;
};

export interface LiveDiscoverySession {
  id: number;
  author: LiveUser;
  title: string;
  kind: LiveKind;
  startedAt: string;
}

export interface CursorPage<T> {
  next: string | null;
  previous: string | null;
  results: T[];
}

interface LiveEventBase {
  id: number;
  liveId: number;
  sequence: number;
  createdAt: string;
}

export interface LiveMessageEvent extends LiveEventBase {
  kind: 'message';
  user?: LiveUser;
  body: string;
  images: LiveMessageImage[];
  replyTo?: LiveReplyTarget;
}

export interface LiveReplyTarget {
  id: number;
  sequence: number | null;
  user?: LiveUser;
  body: string | null;
}

export interface LiveDonationEvent extends LiveEventBase {
  kind: 'donation';
  user?: LiveUser;
  body: string;
  donation: {
    id: number;
    balloon: number;
  };
}

export interface LiveEndedEvent extends LiveEventBase {
  kind: 'ended';
  user?: LiveUser;
}

export interface LiveMessageHiddenEvent extends LiveEventBase {
  kind: 'message_hidden';
  body: null;
  targetEventId: number;
  targetSequence: number;
}

export interface LiveMissionChangedEvent extends LiveEventBase {
  kind: 'mission_changed';
  mission: {
    id: number;
    revision: number;
  };
}

export type LiveEvent =
  | LiveMessageEvent
  | LiveDonationEvent
  | LiveEndedEvent
  | LiveMessageHiddenEvent
  | LiveMissionChangedEvent;

export interface LiveEventsResponse {
  latestSequence: number;
  hasMore: boolean;
  events: LiveEvent[];
}

export type LiveDiscoveryResponse = CursorPage<LiveDiscoverySession>;

export interface LiveWebSocketTicket {
  ticket: string;
  expiresInSeconds: number;
}

export interface LiveSharedEpisodeSavePoint {
  sceneIndexes: number[];
  position: number;
}

export interface LiveSharedEpisodePresentationUpdate {
  savePoint?: LiveSharedEpisodeSavePoint;
  viewerHeightPercent?: number;
}

export interface LiveSharedEpisode {
  snapshotId: number;
  story: {
    id: number;
    title: string;
  };
  episode: {
    id: number;
    title: string;
    editorType: 'mobile' | 'pc';
  };
  contentKey: string;
  savePoint: LiveSharedEpisodeSavePoint;
  viewerHeightPercent?: number;
  startedAt: string;
  updatedAt: string;
}

export interface LiveSharedEpisodeProjection {
  sharedEpisodeVersion: number;
  sharedEpisode: LiveSharedEpisode | null;
}

export interface ActivateLiveSharedEpisodePayload {
  requestKey: string;
  expectedVersion: number;
  episodeId: number;
  savePoint: LiveSharedEpisodeSavePoint;
  viewerHeightPercent?: number;
}

export interface UpdateLiveSharedEpisodePresentationPayload {
  requestKey: string;
  expectedVersion: number;
  snapshotId: number;
  savePoint: LiveSharedEpisodeSavePoint;
  viewerHeightPercent?: number;
}

export interface StopLiveSharedEpisodePayload {
  requestKey: string;
  expectedVersion: number;
  snapshotId: number;
}

export interface OpenLiveSessionPayload {
  openingKey: string;
  title?: string;
  kind?: LiveKind;
  mediaMode?: LiveMediaMode;
}

export interface LiveMediaCredential {
  url: string;
  token: string;
  expiresAt: string;
  role: 'author' | 'viewer';
  participantIdentity: string;
}

export interface LiveMediaPublisherCapability {
  mediaMode: LiveMediaMode;
  mediaPipelineVersion: typeof LIVE_MEDIA_PIPELINE_VERSION;
}

export interface SendLiveMessagePayload {
  clientEventKey: string;
  body?: string;
  imageGrantKeys?: string[];
  replyToEventId?: number;
}

export type LiveMessageImage = IEFinderObject<'image'>;

export interface LiveMessageImageUploadGrant {
  key: string;
  url: string;
  params: Record<string, string>;
}

interface LiveMessageImageUploadGrantEnvelope {
  result: LiveMessageImageUploadGrant;
}

export const unwrapLiveMessageImageUploadGrant = (data: unknown) => {
  if (!data || typeof data !== 'object' || !('result' in data)) {
    return data;
  }
  return (data as LiveMessageImageUploadGrantEnvelope).result;
};

export type LiveViewerEntryRestrictionKind = 'none' | 'temporary' | 'permanent';

export interface LiveViewer {
  id: number;
  nickname: string | null;
  picture: IEFinderObject<'image'> | null;
  interactionRestrictedUntil: string | null;
  entryRestriction: {
    kind: LiveViewerEntryRestrictionKind;
    expiresAt: string | null;
  };
}

export type LiveViewerResponse = CursorPage<LiveViewer>;

export type LiveViewerModerationAction =
  | 'interaction_restriction'
  | 'ejection'
  | 'temporary_entry_restriction'
  | 'permanent_entry_restriction';

export type CreateLiveViewerModerationPayload =
  | {
      requestKey: string;
      viewerId: number;
      action: 'interaction_restriction' | 'temporary_entry_restriction';
      durationSeconds: number;
      reason?: string;
    }
  | {
      requestKey: string;
      viewerId: number;
      action: 'ejection' | 'permanent_entry_restriction';
      reason?: string;
    };

export interface LiveViewerModerationResult {
  actionId: number;
  action: LiveViewerModerationAction;
  viewerId: number;
  appliedAt: string;
  expiresAt: string | null;
  reason: string;
}

export interface LiveViewerRestriction {
  id: number;
  viewer: Pick<LiveViewer, 'id' | 'nickname' | 'picture'>;
  kind: 'interaction' | 'entry';
  sourceLiveId: number;
  appliedAt: string;
  expiresAt: string | null;
  reason: string;
}

export type LiveViewerRestrictionResponse = CursorPage<LiveViewerRestriction>;

export interface SendLiveDonationPayload {
  idempotencyKey: string;
  balloon: number;
  message?: string;
}

export const fetchUserLiveSessions = async (userId: number) => {
  const { data } = await api.get<LiveSession[]>(`/users/${userId}/lives/`);
  return data;
};

export const fetchMyLiveSession = async () => {
  const { data } = await api.get<CurrentLiveSession[]>('/users/me/lives/');
  return data[0] ?? null;
};

function validateLivePaginationUrl(next: string) {
  if (!process.env.NEXT_PUBLIC_API_URL) {
    return;
  }

  const localOrigin =
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  const apiBase = new URL(process.env.NEXT_PUBLIC_API_URL, localOrigin);
  const nextUrl = new URL(next, apiBase);
  if (nextUrl.origin !== apiBase.origin) {
    throw new Error('허용되지 않은 라이브 목록 페이지 URL입니다.');
  }
}

export const fetchLiveDiscovery = async (next?: string) => {
  const url = next ?? '/lives/';
  if (next) {
    validateLivePaginationUrl(next);
  }
  const { data } = await api.get<LiveDiscoveryResponse>(url);
  return data;
};

export const openLiveSession = async (payload: OpenLiveSessionPayload) => {
  const { data } = await api.post<CurrentLiveSession>('/lives/', payload);
  return data;
};

export const fetchLiveSession = async (liveId: number) => {
  const { data } = await api.get<LiveSession>(`/lives/${liveId}/`);
  return data;
};

export const fetchLiveSharedEpisodeContent = async (
  liveId: number,
  snapshotId: number
) => {
  const query = toQueryString({ snapshotId });
  const { data } = await api.get<IEpisodeDraft>(
    `/lives/${liveId}/shared-episode/content/${query}`,
    {
      responseType: 'json',
      transformResponse: defaultTransformResponse,
    }
  );
  return data;
};

export const activateLiveSharedEpisode = async (
  liveId: number,
  payload: ActivateLiveSharedEpisodePayload
) => {
  const { data } = await api.put<LiveSharedEpisodeProjection>(
    `/lives/${liveId}/shared-episode/`,
    payload
  );
  return data;
};

export const updateLiveSharedEpisodePresentation = async (
  liveId: number,
  payload: UpdateLiveSharedEpisodePresentationPayload
) => {
  const { data } = await api.patch<LiveSharedEpisodeProjection>(
    `/lives/${liveId}/shared-episode/`,
    payload
  );
  return data;
};

export const stopLiveSharedEpisode = async (
  liveId: number,
  payload: StopLiveSharedEpisodePayload
) => {
  const { data } = await api.post<LiveSharedEpisodeProjection>(
    `/lives/${liveId}/shared-episode-closures/`,
    payload
  );
  return data;
};

export const closeLiveSession = async (liveId: number) => {
  const { data } = await api.post<LiveSession>(`/lives/${liveId}/closure/`);
  return data;
};

export const heartbeatLiveSession = async (liveId: number) => {
  const { data } = await api.post<LiveSession>(`/lives/${liveId}/heartbeats/`);
  return data;
};

export const createLiveWebSocketTicket = async (liveId: number) => {
  const { data } = await api.post<LiveWebSocketTicket>(
    `/lives/${liveId}/websocket-tickets/`
  );
  return data;
};

export const createLiveMediaCredential = async (
  liveId: number,
  publisherCapability?: LiveMediaPublisherCapability
) => {
  const { data } = await api.post<LiveMediaCredential>(
    `/lives/${liveId}/media-credentials/`,
    publisherCapability
  );
  return data;
};

export const fetchLiveEvents = async (
  liveId: number,
  afterSequence?: number,
  limit = 100
) => {
  const query = toQueryString({ afterSequence, limit });
  const { data } = await api.get<LiveEventsResponse>(
    `/lives/${liveId}/events/${query}`
  );
  return data;
};

export const sendLiveMessage = async (
  liveId: number,
  payload: SendLiveMessagePayload
) => {
  const { data } = await api.post<LiveMessageEvent>(
    `/lives/${liveId}/messages/`,
    payload
  );
  return data;
};

export const createLiveMessageImageUpload = async (
  liveId: number,
  filename: string
) => {
  const { data } = await api.post<LiveMessageImageUploadGrant>(
    `/lives/${liveId}/message-image-uploads/`,
    { filename },
    {
      // Presigned form field names such as `x-amz-signature` are opaque.
      // The default API response transform camelizes nested object keys, so
      // unwrap this response without transforming grant.params.
      transformResponse: [
        ...defaultTransformResponse,
        unwrapLiveMessageImageUploadGrant,
      ],
    }
  );
  return data;
};

export const uploadLiveMessageImage = async (liveId: number, file: File) => {
  const normalizedFile = await normalizeImageForUpload(file);
  const grant = await createLiveMessageImageUpload(
    liveId,
    normalizedFile.name
  );
  const form = new FormData();
  Object.entries(grant.params).forEach(([name, value]) => {
    form.append(name, value);
  });
  form.append('file', normalizedFile, normalizedFile.name);

  const response = await fetch(grant.url, {
    method: 'POST',
    body: form,
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`라이브 이미지 업로드에 실패했습니다: ${response.status}`);
  }
  return grant;
};

export const fetchLiveViewers = async (liveId: number, next?: string) => {
  const url = next ?? `/lives/${liveId}/viewers/`;
  if (next) {
    validateLivePaginationUrl(next);
  }
  const { data } = await api.get<LiveViewerResponse>(url);
  return data;
};

export const createLiveViewerModeration = async (
  liveId: number,
  payload: CreateLiveViewerModerationPayload
) => {
  const { data } = await api.post<LiveViewerModerationResult>(
    `/lives/${liveId}/viewer-moderations/`,
    payload
  );
  return data;
};

export const fetchLiveViewerRestrictions = async (next?: string) => {
  const url = next ?? '/live-viewer-restrictions/';
  if (next) {
    validateLivePaginationUrl(next);
  }
  const { data } = await api.get<LiveViewerRestrictionResponse>(url);
  return data;
};

export const deleteLiveViewerRestriction = async (restrictionId: number) => {
  await api.delete(`/live-viewer-restrictions/${restrictionId}/`);
};

export const sendLiveDonation = async (
  liveId: number,
  payload: SendLiveDonationPayload
) => {
  const { data } = await api.post<LiveDonationEvent>(
    `/lives/${liveId}/donations/`,
    payload
  );
  return data;
};

export const getLiveWebSocketUrl = (
  liveId: number,
  ticket: string,
  apiUrl = process.env.NEXT_PUBLIC_API_URL
) => {
  if (!apiUrl) {
    throw new Error('라이브 WebSocket URL을 만들 수 없습니다.');
  }

  const localOrigin =
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  const url = new URL(apiUrl, localOrigin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `/ws/lives/${liveId}/`;
  url.search = '';
  url.hash = '';
  url.searchParams.set('ticket', ticket);
  url.searchParams.set('viewer_count', 'v1');
  url.searchParams.set('moderation', 'v1');
  url.searchParams.set('shared_episode', 'v1');
  return url.toString();
};
